import type {
  AiMixAction,
  ChannelMacros,
  CompPresetId,
  LoudnessTarget,
  SaturationMode,
  SslEqBand,
  StereoWidthPreset,
  StudioEngineBridge,
  StudioSnapshot,
  TrackMeters,
} from '@melodinai/types';

const EMPTY_SNAP: StudioSnapshot = {
  transport: {
    bpm: 120,
    timeSig: [4, 4],
    playing: false,
    recording: false,
    loopOn: false,
    metronome: false,
    playheadBeat: 0,
    loopStart: 0,
    loopEnd: 8,
  },
  project: {
    name: 'Untitled Project',
    view: 'session',
    snap: 'beat',
    pixelsPerBeat: 60,
    activeDetailTab: 'fx',
  },
  tracks: [],
  session: {
    quantize: 'bar',
    sceneCount: 8,
    stopPolicy: 'exclusive',
    defaultLaunchMode: 'trigger',
    capture: false,
    slots: {},
    queuedKeys: [],
    playingByTrack: {},
  },
  selectedTrackId: null,
  selectedAudioClip: null,
  selectedMidiClip: null,
};

let bridge: StudioEngineBridge | null = null;

function defaultMacros(): ChannelMacros {
  return { punch: 0, weight: 0, air: 0, width: 0.5 };
}

function findTrack(snap: StudioSnapshot, id: string) {
  return snap.tracks.find((t) => t.id === id);
}

/** No-op fallback when legacy engine is not loaded (Vite standalone / tests). */
function createStubBridge(): StudioEngineBridge {
  let snap = structuredClone(EMPTY_SNAP);
  const listeners = new Set<(s: StudioSnapshot) => void>();
  const meterListeners = new Set<(m: Record<string, TrackMeters>) => void>();
  const emit = () => listeners.forEach((l) => l(snap));
  const meters: Record<string, TrackMeters> = {};

  const ensureStrip = (id: string) => {
    const t = findTrack(snap, id);
    if (!t) return null;
    if (!t.macros) t.macros = defaultMacros();
    if (!t.fxChain) t.fxChain = [];
    if (!t.sends) t.sends = [0, 0, 0, 0];
    while (t.sends.length < 4) t.sends.push(0);
    const need = ['EQ8', 'Compressor', 'Saturator', 'Utility'] as const;
    need.forEach((type) => {
      if (!t.fxChain!.some((f) => f.type === type)) {
        t.fxChain!.push({ type, enabled: true, params: {} });
      }
    });
    return t;
  };

  return {
    getSnapshot: () => snap,
    subscribe: (listener) => {
      listeners.add(listener);
      listener(snap);
      return () => listeners.delete(listener);
    },
    play: () => { snap.transport.playing = true; emit(); },
    pause: () => { snap.transport.playing = false; emit(); },
    stop: () => { snap.transport.playing = false; snap.transport.playheadBeat = 0; emit(); },
    toggleRecord: () => { snap.transport.recording = !snap.transport.recording; emit(); },
    setBpm: (bpm) => { snap.transport.bpm = bpm; emit(); },
    setView: (view) => { snap.project.view = view; emit(); },
    setSessionQuantize: (q) => { snap.session.quantize = q; emit(); },
    setStopPolicy: (p) => { snap.session.stopPolicy = p; emit(); },
    setLaunchMode: (m) => { snap.session.defaultLaunchMode = m; emit(); },
    toggleSessionCapture: () => { snap.session.capture = !snap.session.capture; emit(); },
    stopAllSessionClips: () => { snap.session.playingByTrack = {}; snap.session.queuedKeys = []; emit(); },
    launchScene: () => emit(),
    launchTrack: () => emit(),
    queueClip: () => emit(),
    stopTrack: (id) => { delete snap.session.playingByTrack[id]; emit(); },
    addTrack: (opts) => {
      const id = 't_' + Math.random().toString(36).slice(2, 8);
      snap.tracks.push({
        id,
        name: opts?.name || `Track ${snap.tracks.length + 1}`,
        color: '#ff9f5a',
        type: opts?.type || 'audio',
        height: 80,
        volume: 1,
        pan: 0,
        muted: false,
        soloed: false,
        armed: false,
        monitoring: false,
        inputType: 'none',
        inputDeviceId: '',
        inputGain: 1,
        regions: [],
        sends: [0, 0, 0, 0],
        macros: defaultMacros(),
        fxChain: [],
      });
      meters[id] = { peakL: 0, peakR: 0, rmsL: 0, rmsR: 0, grDb: 0 };
      emit();
      return id;
    },
    selectTrack: (id) => { snap.selectedTrackId = id; emit(); },
    mountHost: () => {},
    getSelectedAudioClip: () => snap.selectedAudioClip || null,
    previewSelectedAudioClip: () => {},
    openAudioEditor: () => {},
    registerAudioEditorModule: () => {},
    updateAudioClip: (partial) => {
      if (!snap.selectedAudioClip) return;
      snap.selectedAudioClip = { ...snap.selectedAudioClip, ...partial };
      emit();
    },
    getSelectedMidiClip: () => snap.selectedMidiClip || null,
    openMidiEditor: () => {},
    registerMidiEditorModule: () => {},
    updateMidiClip: (partial) => {
      if (!snap.selectedMidiClip) return;
      snap.selectedMidiClip = { ...snap.selectedMidiClip, ...partial };
      emit();
    },
    transformMidi: () => emit(),
    restart: () => emit(),
    continuePlayback: () => { snap.transport.playing = true; emit(); },
    setSwing: (v) => { snap.transport.swing = v; emit(); },
    jumpToMarker: () => emit(),
    addMarker: () => emit(),
    registerDawDevice: () => {},
    saveTrackPreset: () => {},
    setVolume: (id, volume) => {
      const t = findTrack(snap, id);
      if (!t) return;
      t.volume = Math.max(0, Math.min(2, volume));
      emit();
    },
    setPan: (id, pan) => {
      const t = findTrack(snap, id);
      if (!t) return;
      t.pan = Math.max(-1, Math.min(1, pan));
      emit();
    },
    setMute: (id, muted) => {
      const t = findTrack(snap, id);
      if (!t) return;
      t.muted = muted;
      emit();
    },
    setSolo: (id, soloed) => {
      const t = findTrack(snap, id);
      if (!t) return;
      t.soloed = soloed;
      emit();
    },
    setArmed: (id, armed) => {
      const t = findTrack(snap, id);
      if (!t) return;
      t.armed = armed;
      emit();
    },
    setInputGain: (id, gain) => {
      const t = findTrack(snap, id);
      if (!t) return;
      t.inputGain = gain;
      emit();
    },
    setPhaseInvert: (id, invert) => {
      const t = ensureStrip(id);
      if (!t) return;
      t.phaseInvert = invert;
      emit();
    },
    setLowCut: (id, hz) => {
      const t = ensureStrip(id);
      if (!t) return;
      t.lowCutHz = hz;
      emit();
    },
    setSend: (id, index, amount) => {
      const t = findTrack(snap, id);
      if (!t) return;
      if (!t.sends) t.sends = [0, 0, 0, 0];
      while (t.sends.length < 4) t.sends.push(0);
      t.sends[Math.max(0, Math.min(3, index))] = Math.max(0, Math.min(1, amount));
      emit();
    },
    setSendPre: (id, pre) => {
      const t = findTrack(snap, id);
      if (!t) return;
      t.sendPre = pre;
      emit();
    },
    ensureChannelStrip: (id) => { ensureStrip(id); emit(); },
    ensureMasterBus: () => {
      let m = snap.tracks.find((t) => t.type === 'master');
      if (!m) {
        m = {
          id: 'master',
          name: 'Master',
          color: '#ff9f5a',
          type: 'master',
          height: 80,
          volume: 1,
          pan: 0,
          muted: false,
          soloed: false,
          armed: false,
          monitoring: false,
          inputType: 'none',
          inputDeviceId: '',
          inputGain: 1,
          regions: [],
          sends: [0, 0, 0, 0],
          macros: defaultMacros(),
          fxChain: [],
        };
        snap.tracks.push(m);
      }
      ensureStrip(m.id);
      emit();
    },
    setFxParam: (id, fxType, key, value) => {
      const t = ensureStrip(id);
      if (!t?.fxChain) return;
      let fx = t.fxChain.find((f) => f.type === fxType);
      if (!fx) {
        fx = { type: fxType as never, enabled: true, params: {} };
        t.fxChain.push(fx);
      }
      fx.params[key] = value;
      emit();
    },
    setEqBand: (id, bandId, partial) => {
      const t = ensureStrip(id);
      if (!t) return;
      if (!t.fxChain) t.fxChain = [];
      let eq = t.fxChain.find((f) => f.type === 'EQ8');
      if (!eq) {
        eq = { type: 'EQ8', enabled: true, params: { bands: {} } };
        t.fxChain.push(eq);
      }
      const bands = (eq.params.bands as Record<string, unknown>) || {};
      bands[bandId] = { ...(bands[bandId] as object || {}), ...partial };
      eq.params.bands = bands;
      emit();
    },
    setMacro: (id, key, value) => {
      const t = ensureStrip(id);
      if (!t?.macros) return;
      t.macros[key] = Math.max(0, Math.min(1, value));
      emit();
    },
    applyCompPreset: () => emit(),
    setSaturationMode: (id, mode: SaturationMode) => {
      const t = ensureStrip(id);
      if (!t?.fxChain) return;
      const sat = t.fxChain.find((f) => f.type === 'Saturator');
      if (sat) sat.params._sslMode = mode;
      emit();
    },
    setMasterWidthPreset: (_preset: StereoWidthPreset) => emit(),
    setLoudnessTarget: (_target: LoudnessTarget) => emit(),
    applyAiMixAction: (id, action: AiMixAction) => {
      const t = ensureStrip(id);
      if (!t?.macros) return;
      if (action === 'addPunch') t.macros.punch = Math.min(1, t.macros.punch + 0.25);
      if (action === 'addWeight') t.macros.weight = Math.min(1, t.macros.weight + 0.25);
      if (action === 'addAir') t.macros.air = Math.min(1, t.macros.air + 0.25);
      if (action === 'addWidth') t.macros.width = Math.min(1, t.macros.width + 0.2);
      emit();
    },
    getMeters: () => meters,
    subscribeMeters: (listener) => {
      meterListeners.add(listener);
      listener(meters);
      return () => meterListeners.delete(listener);
    },
  };
}

export function attachEngineBridge(): StudioEngineBridge {
  bridge = window.MelodinStudioEngine || createStubBridge();
  return bridge;
}

export function getEngine(): StudioEngineBridge {
  if (!bridge) return attachEngineBridge();
  return bridge;
}

declare global {
  interface Window {
    MelodinStudioEngine?: StudioEngineBridge;
  }
}

export type { CompPresetId, SslEqBand };
